import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { adminApi } from '@/services/api';
import Button from '@/components/ui/Button';
import { formatCurrency } from '@/utils/formatters';
import {
  ShoppingBagIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon,
  ClockIcon,
  SparklesIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '@/hooks/useAuth';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  original_price?: number;
  product_type: 'brand_new' | 'recycled';
  category_id: number;
  category?: {
    id: number;
    name: string;
  };
  shop_id: number;
  shop?: {
    id: number;
    name: string;
  };
  seller_id: number;
  brand?: string;
  is_public: boolean;
  is_featured: boolean;
  priority_rank?: 'premium' | 'general' | 'basic';
  priority_score?: number;
  is_premium?: boolean;
  created_at: string;
  updated_at: string;
}

interface ProductFilters {
  is_public?: boolean;
  category_id?: number;
  product_type?: 'brand_new' | 'recycled';
  search?: string;
}

const ProductManagementPage: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isSuperAdmin } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingPriority, setUpdatingPriority] = useState<number | null>(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [filters]);

  useEffect(() => {
    if (selectedProduct) {
      console.log('selectedProduct changed:', selectedProduct);
      console.log('Priority values:', {
        priority_rank: selectedProduct.priority_rank,
        priority_score: selectedProduct.priority_score,
        is_premium: selectedProduct.is_premium
      });
    }
  }, [selectedProduct]);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await adminApi.getAllProducts();
      let filteredProducts = response.data;

      // Apply filters
      if (filters.is_public !== undefined) {
        filteredProducts = filteredProducts.filter((product: Product) => product.is_public === filters.is_public);
      }
      if (filters.category_id) {
        filteredProducts = filteredProducts.filter((product: Product) => product.category_id === filters.category_id);
      }
      if (filters.product_type) {
        filteredProducts = filteredProducts.filter((product: Product) => product.product_type === filters.product_type);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredProducts = filteredProducts.filter((product: Product) =>
          product.name.toLowerCase().includes(searchLower) ||
          product.description?.toLowerCase().includes(searchLower) ||
          product.brand?.toLowerCase().includes(searchLower) ||
          product.shop?.name.toLowerCase().includes(searchLower)
        );
      }

      setProducts(filteredProducts);
    } catch (error: any) {
      setError(error.detail || 'Failed to fetch products');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApi.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchQuery }));
  };

  const handleFilterChange = (key: keyof ProductFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const handleUpdatePriority = async (
    productId: number,
    priority_rank?: 'premium' | 'general' | 'basic',
    priority_score?: number,
    is_premium?: boolean
  ) => {
    try {
      setUpdatingPriority(productId);
      
      console.log('Updating priority with:', { productId, priority_rank, priority_score, is_premium });
      
      const response = await adminApi.updateProductPriority(productId, {
        priority_rank,
        priority_score,
        is_premium
      });
      
      console.log('Full API response:', response);
      console.log('Response data:', response.data);
      
      const productData = response.data?.product || response.data;
      
      const newPriorityRank = productData?.priority_rank ?? priority_rank ?? selectedProduct?.priority_rank ?? 'general';
      const newPriorityScore = productData?.priority_score ?? priority_score ?? selectedProduct?.priority_score ?? 0;
      const newIsPremium = productData?.is_premium ?? is_premium ?? selectedProduct?.is_premium ?? false;
      
      console.log('Final values to set:', { newPriorityRank, newPriorityScore, newIsPremium });
      
      toast.success('Product priority updated successfully!');
      
      // Update selectedProduct immediately
      if (selectedProduct && selectedProduct.id === productId) {
        const updatedProduct = {
          ...selectedProduct,
          priority_rank: newPriorityRank,
          priority_score: newPriorityScore,
          is_premium: newIsPremium,
        };
        console.log('Immediately updating selectedProduct to:', updatedProduct);
        setSelectedProduct(updatedProduct);
      }
      
      // Update the products list
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.id === productId 
            ? {
                ...product,
                priority_rank: newPriorityRank,
                priority_score: newPriorityScore,
                is_premium: newIsPremium,
              }
            : product
        )
      );
      
      // Refetch to ensure we have the latest data
      fetchProducts().then(() => {
        if (selectedProduct && selectedProduct.id === productId) {
          setProducts(currentProducts => {
            const refreshedProduct = currentProducts.find(p => p.id === productId);
            if (refreshedProduct) {
              setSelectedProduct({ ...refreshedProduct });
            }
            return currentProducts;
          });
        }
      });
    } catch (error: any) {
      console.error('Error updating product priority:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || error.message || 'Failed to update product priority');
    } finally {
      setUpdatingPriority(null);
    }
  };

  const getStatusColor = (isPublic: boolean) => {
    return isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusLabel = (isPublic: boolean) => {
    return isPublic ? 'Public' : 'Private';
  };

  const getProductTypeColor = (type: string) => {
    return type === 'brand_new' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Product Management</h1>
              <p className="mt-2 text-gray-600">
                Manage and control product visibility and ranking
              </p>
              {isSuperAdmin() && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <SparklesIcon className="h-4 w-4 inline mr-1 text-yellow-600" />
                    <strong>Priority & Ranking:</strong> Click "View Details" or "Priority" button on any product to access Priority & Ranking settings. 
                    Set products as Premium, adjust priority scores (0-1000), and control visibility.
                  </p>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </Button>
              <Button onClick={fetchProducts}>
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products by name, description, brand, or shop..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <Button onClick={handleSearch} className="px-6">
              Search
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.is_public === undefined ? '' : filters.is_public.toString()}
                    onChange={(e) => handleFilterChange('is_public', e.target.value === '' ? undefined : e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="false">Private</option>
                    <option value="true">Public</option>
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={filters.category_id || ''}
                    onChange={(e) => handleFilterChange('category_id', e.target.value ? Number(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Product Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type
                  </label>
                  <select
                    value={filters.product_type || ''}
                    onChange={(e) => handleFilterChange('product_type', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Types</option>
                    <option value="brand_new">Brand New</option>
                    <option value="recycled">Recycled</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
              {/* Product Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                  </div>
                  <div className="flex-shrink-0 ml-4 flex flex-col gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product.is_public)}`}>
                      {getStatusLabel(product.is_public)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProductTypeColor(product.product_type)}`}>
                      {product.product_type === 'brand_new' ? 'Brand New' : 'Recycled'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="p-6">
                <div className="space-y-3">
                  {/* Price */}
                  <div className="flex items-center space-x-2">
                    <TagIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(product.price)}
                    </span>
                    {product.original_price && product.original_price > product.price && (
                      <span className="text-xs text-gray-500 line-through">
                        {formatCurrency(product.original_price)}
                      </span>
                    )}
                  </div>

                  {/* Shop */}
                  {product.shop && (
                    <div className="flex items-center space-x-2">
                      <ShoppingBagIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Shop: {product.shop.name}
                      </span>
                    </div>
                  )}

                  {/* Category */}
                  {product.category && (
                    <div className="flex items-center space-x-2">
                      <TagIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Category: {product.category.name}
                      </span>
                    </div>
                  )}

                  {/* Brand */}
                  {product.brand && (
                    <div className="flex items-center space-x-2">
                      <TagIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Brand: {product.brand}
                      </span>
                    </div>
                  )}

                  {/* Featured */}
                  {product.is_featured && (
                    <div className="flex items-center space-x-2">
                      <StarIcon className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-blue-600 font-medium">Featured</span>
                    </div>
                  )}

                  {/* Priority/Ranking */}
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      {product.is_premium ? (
                        <SparklesIcon className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <StarIcon className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {product.priority_rank || 'general'}
                      </span>
                      {product.priority_score !== undefined && (
                        <span className="text-xs text-gray-500">
                          (Score: {product.priority_score})
                        </span>
                      )}
                    </div>
                    {product.is_premium && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                        Premium
                      </span>
                    )}
                  </div>

                  {/* Created Date */}
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Created: {new Date(product.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center mt-6 space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedProduct(product)}
                    className="flex-1"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  
                  {isSuperAdmin() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedProduct(product)}
                      className="flex-1 border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                      title="Manage Priority & Ranking"
                    >
                      <SparklesIcon className="h-4 w-4 mr-2" />
                      Priority
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-12">
            <ShoppingBagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Product Detail Modal */}
        {selectedProduct && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white my-10">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Product Details & Priority Management</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedProduct(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </Button>
                </div>
                
                <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-2">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{selectedProduct.name}</h4>
                    <p className="text-gray-600 mt-1">{selectedProduct.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Price</label>
                      <p className="text-gray-900">{formatCurrency(selectedProduct.price)}</p>
                      {selectedProduct.original_price && selectedProduct.original_price > selectedProduct.price && (
                        <p className="text-sm text-gray-500 line-through">
                          {formatCurrency(selectedProduct.original_price)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Product Type</label>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getProductTypeColor(selectedProduct.product_type)}`}>
                        {selectedProduct.product_type === 'brand_new' ? 'Brand New' : 'Recycled'}
                      </span>
                    </div>
                  </div>

                  {selectedProduct.shop && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Shop</label>
                      <p className="text-gray-900">{selectedProduct.shop.name}</p>
                    </div>
                  )}

                  {selectedProduct.category && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <p className="text-gray-900">{selectedProduct.category.name}</p>
                    </div>
                  )}

                  {selectedProduct.brand && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Brand</label>
                      <p className="text-gray-900">{selectedProduct.brand}</p>
                    </div>
                  )}

                  {/* Priority Management */}
                  {isSuperAdmin() && (
                    <div className="border-t-2 border-yellow-200 pt-6 mt-6 bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-4">
                        <SparklesIcon className="h-5 w-5 text-yellow-600" />
                        <h4 className="text-lg font-bold text-gray-900">Priority & Ranking Settings</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        Control product visibility and ranking. Premium products appear first to buyers.
                      </p>
                      <div className="space-y-3">
                        {/* Priority Rank */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Priority Rank
                          </label>
                          <select
                            key={`rank-${selectedProduct.id}-${selectedProduct.priority_rank}`}
                            value={selectedProduct.priority_rank ?? 'general'}
                            onChange={(e) => {
                              const newRank = e.target.value as 'premium' | 'general' | 'basic';
                              handleUpdatePriority(selectedProduct.id, newRank);
                            }}
                            disabled={updatingPriority === selectedProduct.id}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="basic">Basic</option>
                            <option value="general">General</option>
                            <option value="premium">Premium</option>
                          </select>
                        </div>

                        {/* Priority Score */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Priority Score (0-1000)
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              key={`score-${selectedProduct.id}-${selectedProduct.priority_score}`}
                              type="number"
                              min="0"
                              max="1000"
                              value={selectedProduct.priority_score ?? 0}
                              onChange={(e) => {
                                const score = parseInt(e.target.value) || 0;
                                handleUpdatePriority(selectedProduct.id, undefined, score);
                              }}
                              disabled={updatingPriority === selectedProduct.id}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                            <button
                              onClick={() => handleUpdatePriority(selectedProduct.id, undefined, Math.min((selectedProduct.priority_score ?? 0) + 10, 1000))}
                              disabled={updatingPriority === selectedProduct.id}
                              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              <ArrowUpIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdatePriority(selectedProduct.id, undefined, Math.max((selectedProduct.priority_score ?? 0) - 10, 0))}
                              disabled={updatingPriority === selectedProduct.id}
                              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              <ArrowDownIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Premium Toggle */}
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            Premium Status
                          </label>
                          <button
                            onClick={() => handleUpdatePriority(selectedProduct.id, undefined, undefined, !selectedProduct.is_premium)}
                            disabled={updatingPriority === selectedProduct.id}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              selectedProduct.is_premium ? 'bg-yellow-500' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                selectedProduct.is_premium ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>

                        {updatingPriority === selectedProduct.id && (
                          <div className="text-sm text-gray-500">Updating...</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedProduct.is_public)}`}>
                        {getStatusLabel(selectedProduct.is_public)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created</label>
                      <p className="text-gray-900">{new Date(selectedProduct.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button variant="outline" onClick={() => setSelectedProduct(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagementPage;

